import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Bot, MessageSquare, Lightbulb, AlertTriangle, TrendingUp } from 'lucide-react';

const agentCapabilities = [
  { icon: MessageSquare, title: 'Natural Language Queries', desc: 'Ask questions like "Why did ACOS spike yesterday?" and get instant answers.' },
  { icon: Lightbulb, title: 'Opportunity Detection', desc: 'AI continuously scans for growth opportunities you might miss.' },
  { icon: AlertTriangle, title: 'Issue Alerting', desc: 'Get notified of problems before they impact performance.' },
  { icon: TrendingUp, title: 'Forecasting', desc: 'Predictive models for sales, inventory, and ad spend.' },
];

export default function AIAgentSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.1)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                AI Agent
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Your AI-powered{' '}
                <span className="text-cyan-400 cyan-glow">account analyst.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Ask questions, get insights, and receive recommendations in natural language. Our AI agent understands your account and communicates like a human analyst.
              </p>

              <div className="space-y-4">
                {agentCapabilities.map((cap, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <cap.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white mb-1">{cap.title}</h4>
                      <p className="text-sm text-dark-400">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div 
              className={`transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 pb-4 border-b border-cyan-500/10 mb-4">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Darkforce AI</div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-xs text-dark-500">Online</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-4">
                  <div className="flex justify-end">
                    <div className="max-w-[80%] p-3 rounded-2xl rounded-tr-sm bg-cyan-500/20 text-white text-sm">
                      Why did my ACOS increase 3% this week?
                    </div>
                  </div>

                  <div className="flex justify-start">
                    <div className="max-w-[90%] p-3 rounded-2xl rounded-tl-sm bg-dark-900/50 border border-cyan-500/10 text-dark-300 text-sm">
                      <p className="mb-2">I found the issue. Your ACOS increased due to:</p>
                      <ul className="space-y-1 ml-4 list-disc">
                        <li>Competitor bid increases on your top 5 keywords</li>
                        <li>One underperforming ASIN consuming 12% of spend at 42% ACOS</li>
                      </ul>
                      <p className="mt-2 text-cyan-400">Recommendation: Pause underperformer and increase bids on converting keywords.</p>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {['Apply recommendation', 'Show more details', 'Schedule report'].map((action, i) => (
                      <button 
                        key={i}
                        className="px-3 py-1.5 rounded-full bg-dark-900/50 border border-cyan-500/20 text-xs text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-dark-900/50 border border-cyan-500/10">
                  <input 
                    type="text" 
                    placeholder="Ask anything about your account..."
                    className="flex-1 bg-transparent text-sm text-white placeholder-dark-500 outline-none"
                    readOnly
                  />
                  <button className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400">
                    <MessageSquare className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
